import { ApplicationModuleConfig } from '@auth/app/core/tokens';
import {
  AccessPair,
  ApplicationAccess,
  TenantAccess,
  UserAccess,
} from '@auth/app/core/models/user-access.model';

export function isKnownApplication(
  code: string,
  applicationModules: Record<string, ApplicationModuleConfig>,
): boolean {
  return Boolean(code && applicationModules[code]);
}

export function getModuleConfig(
  code: string,
  applicationModules: Record<string, ApplicationModuleConfig>,
): ApplicationModuleConfig | undefined {
  return applicationModules[code];
}

export function flattenKnownAccessPairs(
  userAccess: UserAccess,
  applicationModules: Record<string, ApplicationModuleConfig>,
): AccessPair[] {
  const pairs: AccessPair[] = [];

  for (const tenant of userAccess.tenants ?? []) {
    for (const application of tenant.applications ?? []) {
      if (isKnownApplication(application.code, applicationModules)) {
        pairs.push({ tenant, application });
      }
    }
  }

  return pairs;
}

export function findAccessPair(
  userAccess: UserAccess,
  tenantId: string,
  applicationId: string,
): AccessPair | undefined {
  for (const tenant of userAccess.tenants ?? []) {
    if (tenant.id !== tenantId && tenant.client_id !== tenantId) {
      continue;
    }
    for (const application of tenant.applications ?? []) {
      if (application.id === applicationId || application.client_id === applicationId) {
        return { tenant, application };
      }
    }
  }
  return undefined;
}

export function tenantOptions(userAccess: UserAccess): TenantAccess[] {
  return userAccess.tenants ?? [];
}

export function applicationOptionsForTenant(
  tenant: TenantAccess | undefined,
  applicationModules: Record<string, ApplicationModuleConfig>,
): ApplicationAccess[] {
  if (!tenant) {
    return [];
  }
  return (tenant.applications ?? []).filter((app) =>
    isKnownApplication(app.code, applicationModules),
  );
}

export interface ApplicationAccessGroup {
  application: ApplicationAccess;
  tenants: TenantAccess[];
}

export function groupKnownAccessByApplication(
  userAccess: UserAccess,
  applicationModules: Record<string, ApplicationModuleConfig>,
): ApplicationAccessGroup[] {
  const groups = new Map<string, ApplicationAccessGroup>();

  for (const tenant of userAccess.tenants ?? []) {
    for (const application of tenant.applications ?? []) {
      if (!isKnownApplication(application.code, applicationModules)) {
        continue;
      }

      const existing = groups.get(application.id);
      if (existing) {
        if (!existing.tenants.some((item) => item.id === tenant.id)) {
          existing.tenants.push(tenant);
        }
        continue;
      }

      groups.set(application.id, {
        application,
        tenants: [tenant],
      });
    }
  }

  return Array.from(groups.values()).sort((a, b) =>
    a.application.name.localeCompare(b.application.name),
  );
}
